import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { range } from '#src/lib/arrays.ts';
import { isType } from '#src/lib/gql/index.ts';
import { qualityNames } from '#src/lib/players.ts';

import { isGoldenWheelTicketCurrency } from '#src/lib/wheelCurrencies.ts';
import { BoxStar } from './BoxStar/index.tsx';
import type { RewardConfigText_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const Stars = styled(Horizontal).attrs({ as: 'span' })`
  gap: 2px;
`;

type Props = {
  anyRewardConfig: RewardConfigText_anyRewardConfigInterface;
};

export const RewardConfigText = ({ anyRewardConfig }: Props) => {
  const { formatNumber } = useIntl();
  const { toMonetaryAmount } = useMonetaryAmount();

  // Support new CardRewardConfig
  if (
    isType(anyRewardConfig, 'CardRewardConfig') &&
    anyRewardConfig.qualities?.length
  ) {
    return qualityNames[anyRewardConfig.qualities[0]];
  }

  if (isType(anyRewardConfig, 'CardRewardConfig') && anyRewardConfig.quality) {
    return qualityNames[anyRewardConfig.quality];
  }

  if (
    isType(anyRewardConfig, 'ProbabilisticBundleRewardConfig') &&
    anyRewardConfig.probabilisticBundleConfig.stars
  ) {
    return (
      <Stars>
        {range(5).map(i => (
          <BoxStar
            key={i}
            rarity={anyRewardConfig.probabilisticBundleConfig.rarity}
            lightened={
              i < (anyRewardConfig.probabilisticBundleConfig.stars ?? 0)
            }
            size={1.5}
          />
        ))}
      </Stars>
    );
  }

  if (
    isType(anyRewardConfig, 'MonetaryRewardConfig') ||
    isType(anyRewardConfig, 'ConversionCreditRewardConfig')
  ) {
    const amount = isType(anyRewardConfig, 'MonetaryRewardConfig')
      ? anyRewardConfig.monetaryAmount
      : anyRewardConfig.maxDiscount;

    const usdAmount = toMonetaryAmount(amount).usd;

    const formattedUsdAmount = (
      <FormattedNumber
        value={usdAmount}
        style="currency"
        currency={FiatCurrency.USD}
        currencyDisplay="narrowSymbol"
        maximumFractionDigits={0}
        notation={usdAmount > 10000 ? 'compact' : 'standard'}
      />
    );

    return formattedUsdAmount;
  }

  let count = 1;
  if (isType(anyRewardConfig, 'CardShardRewardConfig')) {
    count = anyRewardConfig.quantity;
  } else if (
    isType(anyRewardConfig, 'CoinRewardConfig') ||
    isType(anyRewardConfig, 'InGameCurrencyRewardConfig')
  ) {
    count = anyRewardConfig.amount;
    if (
      isType(anyRewardConfig, 'InGameCurrencyRewardConfig') &&
      isGoldenWheelTicketCurrency(anyRewardConfig.currency)
    ) {
      // frontend fix for the 10 golden wheel tickets === 1 golden wheel spin "issue"
      count /= 10;
    }
  }

  const formattedCount = formatNumber(count, {
    maximumFractionDigits: 1,
    notation: count > 10000 ? 'compact' : 'standard',
    compactDisplay: 'short',
  });

  return formattedCount;
};

RewardConfigText.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardConfigText_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on CardRewardConfig {
        id
        qualities
        quality: qualityTyped
      }
      ... on CardShardRewardConfig {
        id
        quantity
      }
      ... on CoinRewardConfig {
        id
        amount
      }
      ... on InGameCurrencyRewardConfig {
        id
        currency
        amount
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
          stars
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<RewardConfigText_anyRewardConfigInterface>,
};
